export type CompanyRating = {
	companyName: string;
	averageRating: number; // 0-5
	totalReviews: number;
};

function loadRatings(): Record<string, CompanyRating> {
	// Default ratings for demo companies
	const defaults: Record<string, CompanyRating> = {
		'GreenWorks': {
			companyName: 'GreenWorks',
			averageRating: 4.5,
			totalReviews: 12
		},
		'UrbanGardeners': {
			companyName: 'UrbanGardeners',
			averageRating: 4.2,
			totalReviews: 8
		},
		'Leaf & Stone': {
			companyName: 'Leaf & Stone',
			averageRating: 4.8,
			totalReviews: 15
		},
		'Garden Pros': {
			companyName: 'Garden Pros',
			averageRating: 4.3,
			totalReviews: 9
		}
	};

	const stored = localStorage.getItem('companyRatings');
	if (stored) {
		try {
			const parsed = JSON.parse(stored);
			// Merge: start with defaults, then overlay with stored data (user data takes precedence)
			// This ensures defaults are available for companies not yet in stored data
			return { ...defaults, ...parsed };
		} catch {
			// Ignore and use defaults
		}
	}

	return defaults;
}

export let companyRatings: Record<string, CompanyRating> = loadRatings();

function saveRatings() {
	try {
		localStorage.setItem('companyRatings', JSON.stringify(companyRatings));
	} catch (err) {
		console.error('Failed to save company ratings to localStorage', err);
		throw err;
	}
}

export function getCompanyRating(companyName: string): CompanyRating | null {
	companyRatings = loadRatings();
	return companyRatings[companyName] || null;
}

// Generate a deterministic "random" number between 4.0 and 5.0 based on company name
function generateRandomRating(companyName: string): number {
	// Simple hash function to convert company name to a number
	let hash = 0;
	for (let i = 0; i < companyName.length; i++) {
		const char = companyName.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32-bit integer
	}
	// Convert hash to a number between 4.0 and 5.0
	const normalized = Math.abs(hash) % 1000 / 1000; // 0.0 to 1.0
	return 4.0 + normalized; // 4.0 to 5.0
}

export function getAverageRating(companyName: string): number {
	const rating = getCompanyRating(companyName);
	if (rating) {
		return rating.averageRating;
	}
	// Generate a random rating between 4.0 and 5.0 for companies without ratings
	return generateRandomRating(companyName);
}

export function getTotalReviews(companyName: string): number {
	const rating = getCompanyRating(companyName);
	if (rating) {
		return rating.totalReviews;
	}
	// Generate a random review count between 5 and 25 for companies without ratings
	const hash = companyName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
	const normalized = Math.abs(hash) % 1000 / 1000;
	return Math.floor(5 + normalized * 20); // 5 to 25 reviews
}

export function updateCompanyRating(companyName: string, newRating: number) {
	companyRatings = loadRatings();
	
	if (!companyRatings[companyName]) {
		companyRatings[companyName] = {
			companyName,
			averageRating: newRating,
			totalReviews: 1
		};
	} else {
		const existing = companyRatings[companyName];
		// Simple average calculation (in a real app, you'd store individual ratings)
		const totalRating = existing.averageRating * existing.totalReviews + newRating;
		const newTotalReviews = existing.totalReviews + 1;
		companyRatings[companyName] = {
			companyName,
			averageRating: totalRating / newTotalReviews,
			totalReviews: newTotalReviews
		};
	}
	
	saveRatings();
}

